/**
 * @copyright 2022 Nuance Communications Inc.
 * All Rights Reserved.
 */

import React from "react";
import { Checkbox, ChoiceGroup, IChoiceGroupOption, Stack, TextField } from "@fluentui/react";
import { HelpStyle, HelpTextCommands, IHelpTextProps, IHelpTextState } from "./HelpTextViewModel";
import { skipInitialRenderEffect } from "../../CustomHooks/SkipInitialRenderEffect";
import { ControlType } from "@nuance/hierarchical-settings-ui-domain";

const options: IChoiceGroupOption[] = [
    { key: "Tooltip", text: "Tooltip" },
    { key: "Label", text: "Secondary label" }
];

const helpTextCommands = new HelpTextCommands();
const cmds = helpTextCommands.Cmds;

/**
 * HelpText component.
 *
 * @param props Properties for the HelpText component
 * @returns Rendered HelpText component
 */
const HelpText = (props: IHelpTextProps): JSX.Element => {
    const initialState: IHelpTextState = {
        includeHelpText: props.helperText !== "" || props.tooltipText !== "",
        helpStyle: props.helperText !== "" ? HelpStyle.Label : HelpStyle.Tooltip,
        helpText: props.helperText !== "" ? props.helperText : props.tooltipText,
        helpTextErrorMsg: "",
        touched: false
    };
    const [state, dispatch] = React.useReducer(helpTextCommands.Reducer, initialState);

    const onHelpStyleChange = (
        _evt?: React.FormEvent<HTMLElement | HTMLInputElement>,
        option?: IChoiceGroupOption
    ) => {
        if (option !== undefined) {
            dispatch(cmds.helpStyleChanged(option.key));
        }
    };

    const onHelpTextChange = (
        _evt: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>,
        newValue?: string
    ) => {
        if (newValue !== undefined) {
            dispatch(cmds.helpTextChanged(newValue));
        }
    };

    const onIncludeHelpChange = () => {
        dispatch(cmds.includeHelpTextChanged());
    };

    const postChanges = () => {
        if (props.onHelpTextChange !== undefined) {
            const changeData = helpTextCommands.CalcChangeData(state);
            props.onHelpTextChange(changeData);
        }
    };

    skipInitialRenderEffect(postChanges, [state]);

    return (
        <>
            <Checkbox
                label="Include help text"
                checked={state.includeHelpText}
                onChange={onIncludeHelpChange}
            />
            {state.includeHelpText && (
                <Stack className="ms-motion-slideDownIn ms-motion-slideUpOut">
                    {props.controlType !== ControlType.CheckboxField && (
                        <ChoiceGroup
                            selectedKey={HelpStyle[state.helpStyle]}
                            options={options}
                            label="Help Style"
                            onChange={onHelpStyleChange}
                        />
                    )}
                    <TextField
                        label={
                            state.helpStyle === HelpStyle.Tooltip ? "Tooltip" : "Secondary label"
                        }
                        required
                        value={state.helpText}
                        errorMessage={state.touched ? state.helpTextErrorMsg : ""}
                        onChange={onHelpTextChange}
                        onBlur={() => dispatch(cmds.helpTextBlur())}
                    />
                </Stack>
            )}
        </>
    );
};

export default HelpText;
